import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Eye, FileText, Plus, Save, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { showSaveError, showSaveSuccess } from '@/lib/swal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { config } from '@/core/config'
import { inventoryMovementService } from '@/services/inventoryMovementService'
import type { InventoryMovementLine } from '../types'
import { newInventoryMovementLine } from '../types'
import type { ItemEntity, Warehouse } from '@/api/types'

type BatchOption = { id: number; code: string; reference: string; availableQuantity: number }

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function clampNonNegative(n: number) {
  return Number.isFinite(n) ? Math.max(0, n) : 0
}

function resolveAttachmentUrl(pathOrUrl: string | null | undefined): string {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return encodeURI(raw)
  if (/^data:/i.test(raw)) return raw
  const normalizedPath = raw.replace(/\\/g, '/').replace(/^\/+/, '')
  const base = String(config.api.baseUrl || '').replace(/\/+$/, '')
  if (!base) return encodeURI(`/${normalizedPath}`)
  return encodeURI(`${base}/${normalizedPath}`)
}

export const InventoryMovementEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()

  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()

  const selectedBranchId = Number(user?.selectedBranch?.branchId ?? 0)

  const isNew = !id || id === 'new'
  const masterId = Number(isNew ? 0 : id)
  const isViewMode = !isNew && searchParams.get('view') === '1'

  const [existingLoading, setExistingLoading] = useState(false)

  // Warehouses
  const [sourceWarehouses, setSourceWarehouses] = useState<Warehouse[]>([])
  const [destinationWarehouses, setDestinationWarehouses] = useState<Warehouse[]>([])
  const [sourceWarehouseId, setSourceWarehouseId] = useState<string>('')
  const [destinationWarehouseId, setDestinationWarehouseId] = useState<string>('')

  // Master fields
  const [movementDate, setMovementDate] = useState(todayIso())
  const [remarks, setRemarks] = useState<string>('')
  const [code, setCode] = useState<string>('') // optional in UI; backend still expects it
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  const [attachmentFilePath, setAttachmentFilePath] = useState<string>('')

  const attachmentViewUrl = useMemo(() => resolveAttachmentUrl(attachmentFilePath), [attachmentFilePath])

  // Lines
  const [lines, setLines] = useState<InventoryMovementLine[]>(() => Array.from({ length: 5 }, () => newInventoryMovementLine()))
  const [batchOptionsByLineId, setBatchOptionsByLineId] = useState<Record<string, BatchOption[]>>({})

  const batchesCacheRef = useRef<Record<string, BatchOption[]>>({})
  const existingBatchesLoadedRef = useRef(false)

  const srcWhNum = Number(sourceWarehouseId || 0)
  const destWhNum = Number(destinationWarehouseId || 0)

  // Item options depend on source warehouse
  const [itemEntities, setItemEntities] = useState<ItemEntity[]>([])

  const itemOptions = useMemo(() => {
    return itemEntities.map((it) => {
      const name = language === 'ar' ? it.itemNameAr || it.itemNameEn || it.itemCode : it.itemNameEn || it.itemNameAr || it.itemCode
      return {
        value: String(it.itemId),
        label: `${it.itemCode || ''}${it.itemCode ? ' — ' : ''}${name || ''}`.trim(),
      }
    })
  }, [itemEntities, language])

  const readonlyFieldClass = 'focus-visible:ring-0 focus-visible:ring-offset-0'

  const sourceWarehouseDisplay = useMemo(() => {
    const id = Number(sourceWarehouseId || 0)
    if (!id) return ''
    const w = sourceWarehouses.find((x) => Number(x.id) === id)
    if (!w) return ''
    const c = String(w.code || '').trim()
    const n = String(w.name || '').trim()
    if (c && n) return `${c} — ${n}`
    return n || c || String(w.id)
  }, [sourceWarehouseId, sourceWarehouses])

  const destinationWarehouseDisplay = useMemo(() => {
    const id = Number(destinationWarehouseId || 0)
    if (!id) return ''
    const w = destinationWarehouses.find((x) => Number(x.id) === id)
    if (!w) return ''
    const c = String(w.code || '').trim()
    const n = String(w.name || '').trim()
    if (c && n) return `${c} — ${n}`
    return n || c || String(w.id)
  }, [destinationWarehouseId, destinationWarehouses])

  // Load warehouses for the current (selected) branch.
  // Movement UI me branch dropdown intentionally remove kiya gaya hai.
  useEffect(() => {
    const loadWarehouses = async () => {
      if (!selectedBranchId) {
        setSourceWarehouses([])
        setDestinationWarehouses([])
        return
      }
      try {
        const wh = await inventoryMovementService.getWarehousesByBranchId(selectedBranchId)
        setSourceWarehouses(wh)
        setDestinationWarehouses(wh)
      } catch {
        setSourceWarehouses([])
        setDestinationWarehouses([])
      }
    }
    void loadWarehouses()
  }, [selectedBranchId])

  // Load existing movement (edit mode)
  useEffect(() => {
    if (isNew) return
    setExistingLoading(true)
    existingBatchesLoadedRef.current = false

    const loadExisting = async () => {
      try {
        const master = await inventoryMovementService.getById(masterId)
        setCode(master.code || '')
        setMovementDate(master.movementDate ? String(master.movementDate).slice(0, 10) : todayIso())

        setSourceWarehouseId(String(master.sourceWarehouseId || 0))
        setDestinationWarehouseId(String(master.destinationWarehouseId || 0))
        setRemarks(master.remarks || '')
        setAttachmentFilePath(String(master.attachmentFilePath || '').trim())
        setAttachmentFile(null)

        const apiLines = await inventoryMovementService.getLinesByTransferId(masterId)
        const nextLines: InventoryMovementLine[] = apiLines.map((l) => ({
          ...newInventoryMovementLine(),
          transferId: masterId,
          itemId: String(l.itemId ?? ''),
          batchId: String(l.batchId ?? ''),
          id: String(l.id),
          fromWarehouseId: master.sourceWarehouseId,
          toWarehouseId: master.destinationWarehouseId,
          previousQuantity: Number(l.previousQuantity ?? 0),
          quantityTransfered: Number(l.quantityTransfered ?? 0),
          newQuantity: clampNonNegative(Number(l.previousQuantity ?? 0) - Number(l.quantityTransfered ?? 0)),
          isDeleted: 'false',
        }))
        setLines(nextLines.length ? nextLines : Array.from({ length: 5 }, () => newInventoryMovementLine()))
      } catch (e: any) {
        toast.error(e?.response?.data?.message || t('inventoryMovements.saveFailed', { defaultValue: 'Failed to load' }))
      } finally {
        setExistingLoading(false)
      }
    }

    void loadExisting()
  }, [isNew, masterId, t])

  // When destination warehouse changes, update line destination only.
  useEffect(() => {
    if (existingLoading) return
    const nextDest = Number(destinationWarehouseId || 0)
    setLines((prev) => prev.map((l) => ({ ...l, toWarehouseId: nextDest })))
  }, [destinationWarehouseId, existingLoading])

  // Load items for selected source warehouse
  useEffect(() => {
    if (!srcWhNum) {
      setItemEntities([])
      return
    }
    const loadItems = async () => {
      try {
        const items = await inventoryMovementService.getItemsByWarehouse(srcWhNum)
        setItemEntities(items)
      } catch {
        setItemEntities([])
      }
    }
    void loadItems()
  }, [srcWhNum])

  const handleSourceWarehouseChange = (v: string) => {
    setSourceWarehouseId(v)
    const nextSrc = Number(v || 0)
    // If user previously selected same warehouse as destination, clear it.
    if (destinationWarehouseId && Number(destinationWarehouseId) === nextSrc) {
      setDestinationWarehouseId('')
    }
    setBatchOptionsByLineId({})
    // Changing source warehouse invalidates currently selected item/batch and all quantities.
    setLines((prev) =>
      prev.map((l) => ({
        ...l,
        fromWarehouseId: nextSrc,
        itemId: '',
        batchId: '',
        previousQuantity: 0,
        quantityTransfered: 0,
        newQuantity: 0,
      })),
    )
  }

  const createLine = () => {
    const line = newInventoryMovementLine()
    return {
      ...line,
      transferId: masterId,
      fromWarehouseId: srcWhNum,
      toWarehouseId: destWhNum,
    }
  }

  const addLines = (n: number) => {
    setLines((prev) => [...prev, ...Array.from({ length: n }, () => createLine())])
  }

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index))
  }

  const updateLine = (index: number, patch: Partial<InventoryMovementLine>) => {
    setLines((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], ...patch }
      return next
    })
  }

  const loadBatchesForItem = async (whId: number, itemIdNum: number): Promise<BatchOption[]> => {
    const key = `${whId}-${itemIdNum}`
    if (batchesCacheRef.current[key]) return batchesCacheRef.current[key]
    const batches = await inventoryMovementService.getBatchesByWarehouseAndItem(whId, itemIdNum)
    batchesCacheRef.current[key] = batches
    return batches
  }

  // For edit mode: preload batch options for existing selected lines.
  useEffect(() => {
    if (isNew) return
    if (existingLoading) return
    if (existingBatchesLoadedRef.current) return
    if (!srcWhNum) return
    if (lines.length === 0) return

    existingBatchesLoadedRef.current = true

    // If you change this block later, keep it simple: batches are required only so Select shows the labels.
    void (async () => {
      try {
        const uniqueItemIds = Array.from(new Set(lines.map((l) => l.itemId).filter(Boolean))).map((s) => Number(s))
        const loaded: Record<number, BatchOption[]> = {}
        await Promise.all(
          uniqueItemIds.map(async (itemIdNum) => {
            loaded[itemIdNum] = await loadBatchesForItem(srcWhNum, itemIdNum)
          }),
        )
        const nextByLine: Record<string, BatchOption[]> = {}
        for (const l of lines) {
          if (!l.itemId) continue
          const itemIdNum = Number(l.itemId)
          nextByLine[l.id] = loaded[itemIdNum] || []
        }
        setBatchOptionsByLineId(nextByLine)
      } catch {
        // no-op; user can re-select batch if needed
      }
    })()
  }, [existingLoading, isNew, lines, srcWhNum])

  const handleItemChange = async (index: number, value: string) => {
    const line = lines[index]
    if (!line) return

    updateLine(index, {
      itemId: value,
      batchId: '',
      previousQuantity: 0,
      quantityTransfered: 0,
      newQuantity: 0,
    })

    const itemIdNum = Number(value || 0)
    if (!srcWhNum || !itemIdNum) {
      setBatchOptionsByLineId((prev) => ({ ...prev, [line.id]: [] }))
      return
    }

    try {
      const batches = await inventoryMovementService.getBatchesByWarehouseAndItem(srcWhNum, itemIdNum)
      setBatchOptionsByLineId((prev) => ({ ...prev, [line.id]: batches }))
    } catch {
      setBatchOptionsByLineId((prev) => ({ ...prev, [line.id]: [] }))
    }
  }

  const handleBatchChange = async (index: number, value: string) => {
    const line = lines[index]
    if (!line) return
    let availableQuantity = 0
    try {
      if (!srcWhNum || !line.itemId) throw new Error('missing params')
      const details = await inventoryMovementService.getItemDetails(srcWhNum, Number(line.itemId), Number(value))
      availableQuantity = Number(details.availableQuantity ?? 0)
    } catch {
      availableQuantity = 0
    }

    const prevTransferQty = Number(line.quantityTransfered ?? 0)
    const nextBalance = clampNonNegative(availableQuantity - prevTransferQty)

    updateLine(index, { batchId: value, previousQuantity: availableQuantity, newQuantity: nextBalance })
  }

  const handleQtyTransferChange = (index: number, raw: string) => {
    const qty = Number(raw)
    const line = lines[index]
    if (!line) return
    const previous = Number(line.previousQuantity || 0)
    const nextBalance = clampNonNegative(previous - (Number.isFinite(qty) ? qty : 0))

    updateLine(index, {
      quantityTransfered: Number.isFinite(qty) ? qty : 0,
      newQuantity: nextBalance,
    })
  }

  const handleBack = () => navigate('/inventory/movements')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isViewMode) return

    if (!srcWhNum || !destWhNum) {
      toast.error(t('inventoryMovements.validationWarehouses'))
      return
    }
    if (srcWhNum === destWhNum) {
      toast.error(t('inventoryMovements.validationDifferentWarehouses'))
      return
    }

    const filledLines = lines.filter((l) => {
      const itemIdNum = Number(l.itemId || 0)
      const batchIdNum = Number(l.batchId || 0)
      const qty = Number(l.quantityTransfered || 0)
      return itemIdNum > 0 && batchIdNum > 0 && qty > 0
    })

    if (filledLines.length === 0) {
      toast.error('Please add at least one moved item line.')
      return
    }

    const srcWh = sourceWarehouses.find((w) => Number(w.id) === srcWhNum)
    const destWh = destinationWarehouses.find((w) => Number(w.id) === destWhNum)
    const srcLabel = (srcWh?.name || srcWh?.code || '').trim()
    const destLabel = (destWh?.name || destWh?.code || '').trim()
    const whRoute = [srcLabel, destLabel].filter(Boolean).join(' → ')
    const branchLabel = (user?.selectedBranch?.branchName || '').trim()
    const dateStr = (movementDate || '').trim()
    const codeForApi =
      code.trim() || whRoute || branchLabel || dateStr || `IM-${Date.now()}`

    const payload = {
      id: masterId,
      code: codeForApi,
      sourceWarehouseId: srcWhNum,
      destinationWarehouseId: destWhNum,
      movementDate,
      movementType: 0,
      remarks,
      branchId: selectedBranchId,
      attachmentFile,
      itemList: filledLines.map((l) => ({
        // UI uses string ids; backend expects int. New lines should be 0.
        id: Number.isFinite(Number(l.id)) ? Number(l.id) : 0,
        transferId: masterId,
        itemId: Number(l.itemId || 0),
        batchId: Number(l.batchId || 0),
        fromWarehouseId: srcWhNum,
        toWarehouseId: destWhNum,
        previousQuantity: Number(l.previousQuantity || 0),
        quantityTransfered: Number(l.quantityTransfered || 0),
        newQuantity: Number(l.newQuantity || 0),
        isDeleted: l.isDeleted,
      })),
    }

    try {
      if (isNew) {
        await inventoryMovementService.save(payload as any)
      } else {
        await inventoryMovementService.update(payload as any)
      }
      await showSaveSuccess(
        !isNew,
        !isNew
          ? t('inventoryMovements.saveSuccessUpdated', { defaultValue: 'Inventory movement updated successfully.' })
          : t('inventoryMovements.saveSuccessCreated', { defaultValue: 'Inventory movement saved successfully.' }),
      )
      navigate('/inventory/movements')
    } catch (e: unknown) {
      await showSaveError(e, !isNew)
    }
  }

  const tableHeaderClass =
    'h-11 border-0 bg-primary/5 text-xs font-semibold uppercase tracking-wide text-primary hover:bg-primary/5 dark:bg-primary/10 dark:text-primary'

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                {t('inventoryMovements.title')}
              </h1>
              {isViewMode ? (
                <span className="rounded-md border bg-muted/30 px-2 py-1 text-xs font-medium text-muted-foreground">
                  {t('inventoryMovements.viewBadge')}
                </span>
              ) : null}
            </div>
            <p className="text-muted-foreground text-sm">
              {isViewMode ? t('inventoryMovements.viewFormSubtitle') : t('inventoryMovements.formSubtitle')}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('inventoryMovements.sectionTransferDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
            <div className="space-y-2 md:col-span-1">
              <Label htmlFor="movement-date">{t('inventoryMovements.transferDate')}</Label>
              <Input
                id="movement-date"
                type="date"
                value={movementDate}
                readOnly={isViewMode}
                className={isViewMode ? readonlyFieldClass : undefined}
                onChange={(e) => setMovementDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('inventoryMovements.sourceWarehouse')}</Label>
              {isViewMode ? (
                <Input value={sourceWarehouseDisplay} readOnly className={readonlyFieldClass} />
              ) : (
                <Select value={sourceWarehouseId || undefined} onValueChange={handleSourceWarehouseChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('inventoryMovements.selectSource')} />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceWarehouses.map((w) => (
                      <SelectItem key={w.id} value={String(w.id)}>
                        {w.name || w.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t('inventoryMovements.destinationWarehouse')}</Label>
              {isViewMode ? (
                <Input value={destinationWarehouseDisplay} readOnly className={readonlyFieldClass} />
              ) : (
                <Select value={destinationWarehouseId || undefined} onValueChange={setDestinationWarehouseId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('inventoryMovements.selectDestination')} />
                  </SelectTrigger>
                  <SelectContent>
                    {destinationWarehouses
                      .filter((w) => w.id !== Number(sourceWarehouseId || 0))
                      .map((w) => (
                        <SelectItem key={w.id} value={String(w.id)}>
                          {w.name || w.code}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2 md:col-span-1">
              <Label htmlFor="remarks">{t('inventoryMovements.remarks')}</Label>
              <Input
                id="remarks"
                value={remarks}
                readOnly={isViewMode}
                className={isViewMode ? readonlyFieldClass : undefined}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>

            <div className="space-y-2 md:col-span-1">
              {isViewMode ? (
                <>
                  <Label>{t('inventoryMovements.file')}</Label>
                  {attachmentViewUrl ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 w-full justify-start gap-2 text-left font-normal"
                      asChild
                    >
                      <a
                        href={attachmentViewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t('inventoryMovements.viewDocument')}
                      >
                        <Eye className="h-4 w-4" />
                        <span>{t('inventoryMovements.viewDocument')}</span>
                      </a>
                    </Button>
                  ) : (
                    <div className="flex h-10 w-full items-center rounded-md border bg-muted/20 px-3 text-sm text-muted-foreground">
                      —
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Label>{t('inventoryMovements.file')}</Label>
                  <Input
                    type="file"
                    onChange={(e) => setAttachmentFile(e.target.files?.[0] ?? null)}
                    aria-label={t('inventoryMovements.browse')}
                  />
                  <div className="text-xs text-muted-foreground">
                    {attachmentFile ? attachmentFile.name : t('inventoryMovements.noFileChosen')}
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="flex flex-col gap-4 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">{t('inventoryMovements.sectionTransferredItems')}</CardTitle>
            {!isViewMode ? (
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)}>
                  <Plus className="mr-1 h-4 w-4" />
                  {t('inventoryMovements.addRow')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)}>
                  {t('inventoryMovements.add3Rows')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)}>
                  {t('inventoryMovements.add5Rows')}
                </Button>
              </div>
            ) : null}
          </CardHeader>

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className={tableHeaderClass}>{t('inventoryMovements.colItem')}</TableHead>
                    <TableHead className={tableHeaderClass}>{t('inventoryMovements.colBatch')}</TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>{t('inventoryMovements.colCurrentQty')}</TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>{t('inventoryMovements.colQtyTransfer')}</TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>{t('inventoryMovements.colBalanceQty')}</TableHead>
                    {!isViewMode ? (
                      <TableHead className={`${tableHeaderClass} w-[64px] text-center`}>{t('common.delete')}</TableHead>
                    ) : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line, index) => {
                    const batchOptions = batchOptionsByLineId[line.id] || []
                    const itemLabel = line.itemId
                      ? itemOptions.find((o) => o.value === line.itemId)?.label ?? line.itemId
                      : ''
                    const batchOpt = batchOptions.find((b) => String(b.id) === line.batchId)
                    const batchLabelStr = batchOpt
                      ? batchOpt.code
                        ? `${batchOpt.code} — ${batchOpt.reference}`
                        : batchOpt.reference
                      : line.batchId || ''
                    return (
                      <TableRow key={line.id}>
                        <TableCell className="align-top py-2">
                          {isViewMode ? (
                            <Input value={itemLabel} readOnly className={`h-9 ${readonlyFieldClass}`} />
                          ) : (
                            <Select
                              value={line.itemId || undefined}
                              onValueChange={(v) => void handleItemChange(index, v)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder={t('inventoryMovements.selectItem')} />
                              </SelectTrigger>
                              <SelectContent>
                                {itemOptions.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>

                        <TableCell className="align-top py-2">
                          {isViewMode ? (
                            <Input value={batchLabelStr} readOnly className={`h-9 ${readonlyFieldClass}`} />
                          ) : (
                            <Select
                              value={line.batchId || undefined}
                              onValueChange={(v) => void handleBatchChange(index, v)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder={t('inventoryMovements.selectBatch')} />
                              </SelectTrigger>
                              <SelectContent>
                                {batchOptions.map((b) => (
                                  <SelectItem key={b.id} value={String(b.id)}>
                                    {b.code ? `${b.code} — ${b.reference}` : b.reference}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>

                        <TableCell className="py-2 text-center align-top">
                          <Input
                            readOnly
                            className={`h-9 text-center ${isViewMode ? readonlyFieldClass : 'bg-muted/50'}`}
                            value={line.itemId ? String(line.previousQuantity ?? 0) : ''}
                          />
                        </TableCell>

                        <TableCell className="py-2 text-center align-top">
                          <Input
                            type="number"
                            min={0}
                            className={`h-9 text-center ${isViewMode ? readonlyFieldClass : ''}`}
                            value={line.batchId ? String(line.quantityTransfered ?? 0) : ''}
                            readOnly={isViewMode}
                            onChange={(e) => handleQtyTransferChange(index, e.target.value)}
                            disabled={!isViewMode && !line.batchId}
                          />
                        </TableCell>

                        <TableCell className="py-2 text-center align-top">
                          <Input
                            readOnly
                            className={`h-9 text-center ${isViewMode ? readonlyFieldClass : 'bg-muted/50'}`}
                            value={line.batchId ? String(line.newQuantity ?? 0) : ''}
                          />
                        </TableCell>

                        {!isViewMode ? (
                          <TableCell className="py-2 text-center align-top">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:bg-destructive/10"
                              onClick={() => removeLine(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.back')}
          </Button>
          {!isViewMode ? (
            <Button type="submit">
              <Save className="mr-2 h-4 w-4" />
              {!isNew ? t('common.update') : t('inventoryMovements.save')}
            </Button>
          ) : null}
        </div>
      </form>
    </div>
  )
}

